import {Validators} from '@angular/forms';

import {AccountSubTypeEnum, UserBehaviourTypesEnum, AccountCloseoutAlgorithm} from '@spa/api';

import {
    AmountMaxValidator,
    AmountMinValidator,
    InArrayValidator,
    LatinDigitsValidator,
    WhitespaceValidator
} from '@spa/validators';

import {ColorCodesEnum, ComponentTypesEnum, FeatureEnum} from '../../app/shared/enums';
import {
    AmountControl,
    BaseControl,
    CheckboxControl,
    Helper,
    SelectControl,
    SlideToggleControl,
    TextsHelper
} from '../../app/shared/classes';

export const TradingAccountsCashTypeSelector = 'accountType';
export const TradingAccountsCashPortalSelector = 'accountPortal';
export const TradingAccountsCashCurrencySelector = 'accountCurrency';
export const TradingAccountsCashGroupSelector = 'accountGroup';
export const TradingAccountsCashDisplayName = 'accountDisplayName';
export const TradingAccountsCashAlias = 'accountDisplayAlias';
export const TradingAccountsCashAccountReferenceId = 'accountReferenceId';
export const TradingAccountsAccountSubType = 'accountSubType';

// New selectors for the risk limits section
export const TradingAccountsFirmIdSelector = 'firmId';
export const TradingAccountsGroupIdSelector = 'groupId';
export const TradingAccountsChangeEventEmailSelector = 'changeEventEmail';
export const TradingAccountsIncrementalUpdateSelector = 'incrementalUpdate';
export const TradingAccountsDisabledSelector = 'disabled';
export const TradingAccountsSkipFundingSelector = 'skipFunding';

// All Asset Classes - Total Dyn Exp
export const TradingAccountsTotalDynExpLimitSelector = 'totalDynExpLimit';
export const TradingAccountsTotalDynExpMultSelector = 'totalDynExpMult';
export const TradingAccountsTotalDynExpActionSelector = 'totalDynExpAction';

// All Asset Classes - Loss Limit
export const TradingAccountsLossLimitSelector = 'lossLimit';
export const TradingAccountsLossLimitActionSelector = 'lossLimitAction';

// All Asset Classes - Pos Limit
export const TradingAccountsPosLimitSelector = 'posLimit';
export const TradingAccountsPosLimitActionSelector = 'posLimitAction';

// Equities - Short Sale
export const TradingAccountsShortSaleActionSelector = 'shortSaleAction';

// Equities - Max Order Qty
export const TradingAccountsMaxOrderQtySelector = 'maxOrderQty';
export const TradingAccountsMaxOrderQtyActionSelector = 'maxOrderQtyAction';

// Equities - Min Adv
export const TradingAccountsMinAdvSelector = 'minAdv';
export const TradingAccountsMinAdvActionSelector = 'minAdvAction';

export const  SelectorActionDoNothing = 'SelectorActionDoNothing';
export const  SelectorActionRejectOrder = 'SelectorActionRejectOrder';
export const  SelectorActionPopupWarning = 'SelectorActionPopupWarning';
export const  SelectorActionSendEmail = 'SelectorActionSendEmail';
export const  SelectorActionSendSms = 'SelectorActionSendSms';
export const  SelectorActionSetToCloseout = 'SelectorActionSetToCloseout';
export const  SelectorActionSetToBlock = 'SelectorActionSetToBlock';



export const TradingAccountsTextFieldsMinLength = 3;

export interface TradingAccountsCashControlsInterface {
    type: SelectControl;
    portals: SelectControl;
    currency: SelectControl;
    groups: SelectControl;
    displayName: BaseControl;
    alias: BaseControl;
    accountReferenceId: BaseControl;
    accountSubType: SelectControl;
}

export const TradingAccountsCashControls = (
    getForEditing = false,
    floatingLabelNever = false
): TradingAccountsCashControlsInterface => {
    const controls = {
        type: new SelectControl({
            name: TradingAccountsCashTypeSelector,
            value: Helper.textsHelper().getTradingAccountTypeName(true),
            placeholder: 'Account Type',
            validators: [],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getTradingAccountTypeName(true),
                    value: Helper.textsHelper().getTradingAccountTypeName(true),
                }
            ],
            disabled: true,
            floatingLabelNever
        }),
        portals: new SelectControl({
            name: TradingAccountsCashPortalSelector,
            value: null,
            placeholder: 'Portal',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        currency: new SelectControl({
            name: TradingAccountsCashCurrencySelector,
            value: null,
            placeholder: 'Currency',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        groups: new SelectControl({
            name: TradingAccountsCashGroupSelector,
            value: null,
            placeholder: 'Group',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        displayName: new BaseControl({
            name: TradingAccountsCashDisplayName,
            value: null,
            placeholder: 'Display name',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        alias: new BaseControl({
            name: TradingAccountsCashAlias,
            value: null,
            placeholder: 'Alias',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        accountReferenceId: new BaseControl({
            name: TradingAccountsCashAccountReferenceId,
            value: null,
            placeholder: 'Account Reference Id',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        accountSubType: new SelectControl({
            name: TradingAccountsAccountSubType,
            value: null,
            placeholder: 'Account Sub Type',
            validators: [
                Validators.required,
                InArrayValidator([AccountSubTypeEnum.TradingCash, AccountSubTypeEnum.FundCash])
            ],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.TradingCash),
                    value: AccountSubTypeEnum.TradingCash,
                },
                {
                    label: Helper.textsHelper().getAccountSubTypeName(AccountSubTypeEnum.FundCash),
                    value: AccountSubTypeEnum.FundCash,
                }
            ],
            floatingLabelNever
        }),
    };

    if (!getForEditing) {
        return controls;
    }

    delete controls.type;
    delete controls.portals;
    delete controls.groups;

    return controls;
};

export const TradingAccountsTradingTypeSelector = 'accountType';
export const TradingAccountsTradingPortalSelector = 'accountPortal';
export const TradingAccountsTradingCurrencySelector = 'accountCurrency';
export const TradingAccountsTradingPlatformSelector = 'accountPlatform';
export const TradingAccountsTradingDisplayNameSelector = 'accountDisplayName';
export const TradingAccountsTradingClearingAgentSelector = 'accountClearingAgent';
export const TradingAccountsTradingClearingAccountRefIdSelector = 'accountClearingAgentRefId';
export const TradingAccountsTradingAlias = 'accountDisplayAlias';
export const TradingAccountsTradingAccountReferenceId = 'accountReferenceId';
export const TradingAccountsTradingClearingAccountRefIdSLength = 40;
export const TradingAccountsExecutionBrokerSelector = 'executionBroker';
export const TradingAccountsExecutionRouteSetsSelector = 'executionRouteSets';
export const TradingAccountsExecutionRoutesSelector = 'executionRoutes';
export const  SelectorDailyBuyingPowerMultiple = 'SelectorDailyBuyingPowerMultiple';
export const  SelectorOvernightBuyingPowerMultiple = 'SelectorOvernightBuyingPowerMultiple';
export const TradingAccountsVlrPercentSelector = 'vlrPercent';
export const TradingAccountsNumberOfAccountsSelector = 'numberOfAccounts';
export const TradingAccountsMaxNumberOfAccounts = 20;


export interface TradingAccountsTradingControlsInterface {
    displayName: BaseControl;
    alias: BaseControl;
    accountReferenceId: BaseControl;
    clearingAgent: SelectControl;
    clearingAccountRefId: BaseControl;
    tradingPlatforms: SelectControl;

    broker: SelectControl;
    routeSets: SelectControl;
    routes: CheckboxControl[];
    type: SelectControl;
    portals: SelectControl;
    currency: SelectControl;
    accountSubType: SelectControl;

    // New properties for risk limits
    firmId?: SelectControl;
    groupId?: SelectControl;
    disabled?: CheckboxControl;
    skipFunding?: CheckboxControl;

    // Batch creation
    numberOfAccounts?: AmountControl;

    // All Asset Classes - Total Dyn Exp
    totalDynExpLimit?: AmountControl;
    totalDynExpMult?: AmountControl;
    totalDynExpAction?: SelectControl;

    // All Asset Classes - Loss Limit
    lossLimit?: AmountControl;
    lossLimitAction?: SelectControl;

    // All Asset Classes - Pos Limit
    posLimit?: AmountControl;
    posLimitAction?: SelectControl;

    // Equities - Short Sale
    shortSaleAction?: SelectControl;

    // All Asset Classes - Max Order Qty
    maxOrderQty?: AmountControl;
    maxOrderQtyAction?: SelectControl;

    vlrPercent?: AmountControl;
}

export const GetMarginActionSelectControl = (name: string, floatingLabelNever: boolean) : SelectControl =>{
    return new SelectControl({
        name: name,
        value: [] as any,
        placeholder: 'Action',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        multiple: true,
        floatingLabelNever,
        options: [
            {
                label: 'Do Nothing',
                value: SelectorActionDoNothing,
            },
            {
                label: 'Reject Order',
                value: SelectorActionRejectOrder,
            },
            {
                label: 'Popup Warning',
                value: SelectorActionPopupWarning,
            },
            {
                label: 'Send Email',
                value: SelectorActionSendEmail,
            },
            {
                label: 'Send Sms',
                value: SelectorActionSendSms,
            },
            {
                label: 'Set to Closeout',
                value: SelectorActionSetToCloseout,
            },
            {
                label: 'Set to Block',
                value: SelectorActionSetToBlock,
            }]
    });
}

export const TradingAccountsTradingControls = (
    isFundAccount: boolean,
    getForEditing = false,
    floatingLabelNever= false,
): TradingAccountsTradingControlsInterface => {
    const controls = {
        type: new SelectControl({
            name: TradingAccountsTradingTypeSelector,
            value: Helper.textsHelper().getTradingAccountTypeName(false),
            placeholder: 'Account Type',
            validators: [],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getTradingAccountTypeName(false),
                    value: Helper.textsHelper().getTradingAccountTypeName(false),
                }
            ],
            disabled: true,
            floatingLabelNever
        }),
        portals: new SelectControl({
            name: TradingAccountsTradingPortalSelector,
            value: null,
            placeholder: 'Portal',
            validators: [
                Validators.required,
                InArrayValidator([
                    UserBehaviourTypesEnum.Retail,
                    UserBehaviourTypesEnum.Pro
                ])],
            componentType: ComponentTypesEnum.Select,
            options: [
                {
                    label: Helper.textsHelper().getUserTypeBehaviourName(UserBehaviourTypesEnum.Retail),
                    value: UserBehaviourTypesEnum.Retail
                },
                {
                    label: Helper.textsHelper().getUserTypeBehaviourName(UserBehaviourTypesEnum.Pro),
                    value: UserBehaviourTypesEnum.Pro
                }
            ],
            floatingLabelNever
        }),
        tradingPlatforms: new SelectControl({
            name: TradingAccountsTradingPlatformSelector,
            value: null,
            placeholder: 'Platform',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        currency: new SelectControl({
            name: TradingAccountsTradingCurrencySelector,
            value: null,
            placeholder: 'Currency',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        displayName: new BaseControl({
            name: TradingAccountsTradingDisplayNameSelector,
            value: null,
            placeholder: 'Display name',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        clearingAgent: new SelectControl({
            name: TradingAccountsTradingClearingAgentSelector,
            value: null,
            placeholder: 'Clearing agent',
            validators: !isFundAccount ? [Validators.required] : [],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        clearingAccountRefId: new BaseControl({
            name: TradingAccountsTradingClearingAccountRefIdSelector,
            value: null,
            placeholder: 'Clearing account id',
            validators: !isFundAccount ? [WhitespaceValidator(), LatinDigitsValidator(), Validators.maxLength(TradingAccountsTradingClearingAccountRefIdSLength)] : [Validators.maxLength(TradingAccountsTradingClearingAccountRefIdSLength)],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        alias: new BaseControl({
            name: TradingAccountsTradingAlias,
            value: null,
            placeholder: 'Alias',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        accountReferenceId: new BaseControl({
            name: TradingAccountsTradingAccountReferenceId,
            value: null,
            placeholder: 'Account Reference Id',
            validators: [Validators.minLength(TradingAccountsTextFieldsMinLength), LatinDigitsValidator()],
            componentType: ComponentTypesEnum.Text,
            floatingLabelNever
        }),
        accountSubType: new SelectControl({
            name: TradingAccountsAccountSubType,
            value: null,
            placeholder: 'Account Sub Type',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        broker: new SelectControl({
            name: TradingAccountsExecutionBrokerSelector,
            value: null,
            placeholder: 'Execution Broker',
            validators: [],
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        routeSets: new SelectControl({
            name: TradingAccountsExecutionRouteSetsSelector,
            value: null,
            placeholder: 'Route Sets',
            validators: [],
            multiple: true,
            componentType: ComponentTypesEnum.Select,
            options: [],
            floatingLabelNever
        }),
        routes: [],

        // Risk Limits - Basic Settings
        firmId: new SelectControl({
            name: TradingAccountsFirmIdSelector,
            value: null,
            placeholder: 'OMS Firm',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            floatingLabelNever,
            options: [],
            isDynamic: true
        }),
        groupId: new SelectControl({
            name: TradingAccountsGroupIdSelector,
            value: null,
            placeholder: 'OMS Group ID',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Select,
            floatingLabelNever,
            options: [],
            isDynamic: true
        }),
        disabled: new CheckboxControl({
            name: TradingAccountsDisabledSelector,
            value: null,
            placeholder: 'Create as Disabled',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            checked: false,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            floatingLabelNever: false
        }),
        skipFunding: new CheckboxControl({
            name: TradingAccountsSkipFundingSelector,
            value: null,
            placeholder: 'Create without funding',
            validators: [],
            componentType: ComponentTypesEnum.Checkbox,
            checked: false,
            color: ColorCodesEnum.Primary,
            expectBooleanValue: true,
            floatingLabelNever: false
        }),

        // All Asset Classes - Total Dynamic Exposure
        totalDynExpLimit: new AmountControl({
            name: TradingAccountsTotalDynExpLimitSelector,
            value: null,
            placeholder: 'Total Dyn Exp Limit',
            validators: [AmountMinValidator(1), AmountMaxValidator(100)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: '',
        }),
        totalDynExpMult: new AmountControl({
            name: TradingAccountsTotalDynExpMultSelector,
            value: null,
            placeholder: 'Multiplier',
            validators: [AmountMinValidator(1), AmountMaxValidator(100)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: ''
        }),
        totalDynExpAction: GetMarginActionSelectControl(TradingAccountsTotalDynExpActionSelector, floatingLabelNever),

        // All Asset Classes - Loss Limit
        lossLimit: new AmountControl({
            name: TradingAccountsLossLimitSelector,
            value: null,
            placeholder: 'Loss Limit',
            validators: [AmountMinValidator(1), AmountMaxValidator(10000)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: ''
        }),
        lossLimitAction: GetMarginActionSelectControl(TradingAccountsLossLimitActionSelector, floatingLabelNever),

        // All Asset Classes - Position Limit
        posLimit: new AmountControl({
            name: TradingAccountsPosLimitSelector,
            value: null,
            placeholder: 'Position Limit',
            validators: [AmountMinValidator(1), AmountMaxValidator(10000)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: ''
        }),
        posLimitAction: GetMarginActionSelectControl(TradingAccountsPosLimitActionSelector, floatingLabelNever),

        // Equities - Short Sale
        shortSaleAction: GetMarginActionSelectControl(TradingAccountsShortSaleActionSelector, floatingLabelNever),


        // Equities - Max Order Qty
        maxOrderQty: new AmountControl({
            name: TradingAccountsMaxOrderQtySelector,
            value: null,
            placeholder: 'Max Order Qty',
            validators: [AmountMinValidator(1), AmountMaxValidator(10000)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: ''
        }),
        maxOrderQtyAction: GetMarginActionSelectControl(TradingAccountsMaxOrderQtyActionSelector, floatingLabelNever),

        vlrPercent: new AmountControl({
            name: TradingAccountsVlrPercentSelector,
            value: null,
            placeholder: 'Locates markup (%)',
            validators: [AmountMinValidator(0), AmountMaxValidator(100.00)],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            isPercentage: true,
            currency: ''
        }),
    };

    if (!getForEditing && !isFundAccount && Helper.isFeatureEnabled(FeatureEnum.BatchAccountCreation)) {
        (controls as TradingAccountsTradingControlsInterface).numberOfAccounts = new AmountControl({
            name: TradingAccountsNumberOfAccountsSelector,
            value: '1',
            placeholder: 'Number of accounts',
            validators: [
                Validators.required,
                AmountMinValidator(1),
                AmountMaxValidator(TradingAccountsMaxNumberOfAccounts)
            ],
            componentType: ComponentTypesEnum.AmountInput,
            floatingLabelNever,
            currency: ''
        });
    }

    if (!getForEditing) {
        return controls;
    }

    delete controls.type;
    delete controls.portals;
    delete controls.routeSets;
    delete controls.routes;
    delete controls.groupId;
    delete controls.firmId;

    return controls;
};

